/**
 * authVocabulary — the operator-facing STRINGS for the RBAC identity chip (Phase 1, #566).
 *
 * Centralised so the vocabulary lock (`authVocabulary.lock.test.ts`) can assert every
 * string is clean of US-ICS terms (no "Area Commander" / PIO / "Unit Leader" / ICS-form)
 * and that the COP role labels are the verbatim Ontario IMS App A terms (ruling R5). The
 * PER-SURFACE doctrine rule (CLAUDE.md): an operator-facing label is STRICT VERBATIM
 * Ontario IMS. Agency names come from the DB (not here) — this module owns only the chip
 * copy + the role→label map.
 */
import type { UserRole } from '@tsr/shared-types';

/** The chip prefix. */
export const SIGNED_IN_AS = 'Signed in as';

/** The middot that separates name · agency · role in the chip. */
export const IDENTITY_SEPARATOR = '·';

/**
 * Operator-facing role DISPLAY labels (ruling R5). The COP (IMS) roles are STRICT
 * VERBATIM Ontario IMS Appendix A — validated against the doctrine mirror + the live
 * ontario.ca source at the post-build proofread:
 *   eoc-director   → "EOC Director"      (App A #26)
 *   situation-unit → "Situation Unit"    (App A #76)
 *   logistics      → "Logistics Section" (App A #48)
 *   liaison        → "Liaison Officer"   (App A #46)
 *   viewer         → "Viewer"            (descriptive — read-only, not a doctrine seat)
 *   admin          → "Admin"             (backend/descriptive — not a COP seat)
 * `drone-operator` is the external PORTAL class (never on the COP; descriptive).
 */
export const ROLE_DISPLAY_LABELS: Record<UserRole, string> = {
  'eoc-director': 'EOC Director',
  'situation-unit': 'Situation Unit',
  logistics: 'Logistics Section',
  liaison: 'Liaison Officer',
  viewer: 'Viewer',
  admin: 'Admin',
  'drone-operator': 'Drone Operator',
};

/** The human label for a role; falls back to the least-privilege "Viewer" label. */
export function roleDisplayLabel(role: UserRole): string {
  return ROLE_DISPLAY_LABELS[role] ?? ROLE_DISPLAY_LABELS.viewer;
}
